import React from "react";
import {
  Grid,
  Typography,
  Avatar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";

const BasicSettingsTab = ({
  t,
  values,
  setFieldValue,
  handleBlur,
  languages,
  languageTab,
  onLanguageTabChange,
  UploadComponent,
  categories,
  products,
  getCategoryDisplayName,
  getProductDisplayName,
}) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(values.isActive)}
              onChange={(e) => setFieldValue("isActive", e.target.checked)}
            />
          }
          label={t("pages.dsokPromotions.active", { defaultValue: "Active" })}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("pages.dsokCategories.image")}
        </Typography>
        <UploadComponent
          name="image"
          value={values.image}
          setFieldValue={setFieldValue}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <Alert variant="outlined" severity="info">
            {t("pages.dsokPromotions.imageSizeInfo")}
          </Alert>
        </Typography>
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("pages.dsokPromotions.imageExample")}
        </Typography>
        <Avatar
          src={"/images/templates/promotion-template.png"}
          variant="rounded"
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: 420,
            objectFit: "contain",
          }}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <TabContext value={languageTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <TabList onChange={onLanguageTabChange} aria-label="language tabs">
              {(languages || []).map((lang) => (
                <Tab key={lang} label={t(`languages.${lang}`)} value={lang} />
              ))}
            </TabList>
          </Box>
          {(languages || []).map((lang) => (
            <TabPanel key={lang} value={lang} sx={{ p: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("pages.dsokPromotions.name", {
                      defaultValue: "Name",
                    })}
                    name={`name.${lang}`}
                    value={values.name?.[lang] || ""}
                    onChange={(e) => {
                      if (e.target.value.length <= 60)
                        setFieldValue(`name.${lang}`, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("pages.dsokPromotions.description", {
                      defaultValue: "Description",
                    })}
                    name={`description.${lang}`}
                    value={values.description?.[lang] || ""}
                    onChange={(e) => {
                      if (e.target.value.length <= 150)
                        setFieldValue(`description.${lang}`, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    multiline
                    minRows={2}
                  />
                </Grid>
              </Grid>
            </TabPanel>
          ))}
        </TabContext>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormControl fullWidth size="small">
          <InputLabel id="target-type-label">
            {t("pages.dsokPromotions.targetType", { defaultValue: "Target" })}
          </InputLabel>
          <Select
            labelId="target-type-label"
            label={t("pages.dsokPromotions.targetType")}
            value={values.targetType}
            name="targetType"
            onChange={(e) => setFieldValue("targetType", e.target.value)}
          >
            <MenuItem value="category">
              {t("pages.dsokPromotions.targetCategory", {
                defaultValue: "Category",
              })}
            </MenuItem>
            <MenuItem value="product">
              {t("pages.dsokPromotions.targetProduct", {
                defaultValue: "Product",
              })}
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {values.targetType === "category" && (
        <Grid item xs={12} md={6} lg={3}>
          <Autocomplete
            fullWidth
            size="small"
            options={categories || []}
            getOptionLabel={(option) => getCategoryDisplayName(option)}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            filterOptions={(options, state) =>
              (options || []).filter((opt) =>
                String(getCategoryDisplayName(opt) || "")
                  .toLowerCase()
                  .includes(String(state.inputValue || "").toLowerCase())
              )
            }
            value={
              (categories || []).find((c) => c._id === values.targetId) || null
            }
            onChange={(e, newValue) =>
              setFieldValue("targetId", newValue?._id || "")
            }
            renderOption={(props, option) => (
              <li
                {...props}
                key={option?._id || getCategoryDisplayName(option)}
              >
                {getCategoryDisplayName(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pages.dsokPromotions.targetCategory", {
                  defaultValue: "Category",
                })}
              />
            )}
          />
        </Grid>
      )}
      {values.targetType === "product" && (
        <Grid item xs={12} md={6} lg={3}>
          <Autocomplete
            fullWidth
            size="small"
            options={products || []}
            getOptionLabel={(option) => getProductDisplayName(option)}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            filterOptions={(options, state) =>
              (options || []).filter((opt) =>
                String(getProductDisplayName(opt) || "")
                  .toLowerCase()
                  .includes(String(state.inputValue || "").toLowerCase())
              )
            }
            value={
              (products || []).find((p) => p._id === values.targetId) || null
            }
            onChange={(e, newValue) =>
              setFieldValue("targetId", newValue?._id || "")
            }
            renderOption={(props, option) => (
              <li {...props} key={option?._id || getProductDisplayName(option)}>
                {getProductDisplayName(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pages.dsokPromotions.targetProduct", {
                  defaultValue: "Product",
                })}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default BasicSettingsTab;
